import { formatDate } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { NgxSpinnerService } from "ngx-spinner";
import { DistributorService } from "src/app/services/distributor.service";
import { UserService } from "src/app/services/user.service";
import { UtilityService } from "src/app/services/utility.service";
import { WorkorderService } from "src/app/services/workorder.service";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

@Component({
  selector: "app-upload-excel",
  templateUrl: "./upload-excel.component.html",
  styleUrls: ["./upload-excel.component.scss"],
})
export class UploadExcelComponent {
  selectedFile: File | null = null;
  fileExtension: string | null = null;
  excelData: any[] = [];
  fileContent: string | null = null;
  isDownloadFile: boolean = false;
  isUploading: boolean = false; // Added for loading state
  finalJson: any = {};
  fileRenderResult: any;

  private basePath: string =
    '../../assets/' + this.utilityService.currentUtility.OfficeId + '/templateExcels/';

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private distributorService: DistributorService,
    public dialogRef: MatDialogRef<UploadExcelComponent>,
    private utilityService: UtilityService,
    private spinnerService: NgxSpinnerService,
    private workorderService: WorkorderService,
    private userService: UserService
  ) { }

  /**
   * Initiates the download of a template file via API.
   */
  downloadImportTemplate(): void {
    this.isDownloadFile = true;
    const caption = this.dialogData.caption !== 'Import Report' ?
      this.dialogData.caption : this.dialogData.response?.ProgramdetailId || '';
    this.workorderService.downloadImportFiles(caption).subscribe({
      next: (response: any) => {
        const fileName = response?.Value || '';
        const filePath = `${this.basePath}${fileName}`;
        const link = document.createElement('a');
        link.href = filePath;
        link.download = fileName;
        link.click();
        this.isDownloadFile = false;
      },
      error: () => {

      },
    });
  }

  handleFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.selectedFile = input.files[0];
    this.fileExtension = this.selectedFile.name.split('.').pop()?.toLowerCase();

    const fileReader = new FileReader();
    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      this.fileContent = fileReader.result as string;
    };
    fileReader.readAsDataURL(this.selectedFile);

    const excelReader = new FileReader();
    excelReader.readAsBinaryString(this.selectedFile);
    excelReader.onload = (e: ProgressEvent<FileReader>) => {
      const binaryString: string = e.target?.result as string;
      const workbook: XLSX.WorkBook = XLSX.read(binaryString, {
        type: 'binary',
        cellDates: true,
      });
      const sheetName: string = workbook.SheetNames[0];
      const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName];
      this.excelData = XLSX.utils.sheet_to_json(worksheet, {
        dateNF: 'yyyy-mm-dd',
        raw: false,
      });
    };
  }


  onFileChange(event: any): void {
    const target: DataTransfer = <DataTransfer>(event.target);
    if (target.files.length !== 1) return;

    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      const bstr: string = e.target.result.split(",")[1];
      this.fileRenderResult = reader.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'base64' });

      //REBATE CALCULATION
      const rebateSheet = wb.Sheets['Rebate Calculation'];
      const data = XLSX.utils.sheet_to_json(rebateSheet, { header: 1 });
      const rebateResult: any = {
        inputs: {},
        incentive: {},
        perMeasureBreakdown: [],
        programRules: {},
        metadata: {}
      };
      let currentSection = 'inputs';
      let tableHeaders: string[] = [];
      for (let i = 0; i < data.length; i++) {
        const row: any = data[i];
        if (!row || row?.length === 0) continue;

        const firstCell = (row[0] || '').toString().trim();
        if (firstCell === 'Incentive') {
          currentSection = 'incentive';
          continue;
        }
        if (firstCell === 'Per Measure Breakdown') {
          currentSection = 'perMeasureBreakdown';
          continue;
        }
        if (firstCell === 'Program Rules') {
          currentSection = 'programRules';
          continue;
        }
        if (['Version:', 'Last updated:', 'Updated by:'].includes(firstCell)) {
          rebateResult.metadata[firstCell.replace(':', '')] = this.cleanValue(row[1]);
          continue;
        }
        // handle perMeasureBreakdown table
        if (currentSection === 'perMeasureBreakdown') {
          if (firstCell === 'Measure') {
            tableHeaders = row.slice(0, 11);
          } else if (tableHeaders.length > 0 && row[0]) {
            const rowObj: any = {};
            tableHeaders.forEach((key, idx) => {
              rowObj[key.trim()] = this.cleanValue(row[idx]);
            });
            rebateResult.perMeasureBreakdown.push(rowObj);
          }
        }
        else if (currentSection === 'programRules') {
          if (firstCell) {
            rebateResult.programRules[firstCell] = this.cleanValue(row[1]);
          }
        }
        else if (currentSection === 'inputs' || currentSection === 'incentive') {
          if (
            firstCell !== 'CNCR Program Total Incentive' &&
            firstCell !== 'Inputs:' &&
            firstCell !== ''
          ) {
            rebateResult[currentSection][firstCell] = this.cleanValue(row[1]);
          }
        }
      }

      //measure summary section
      const measureSheet = wb.Sheets['Measure Summary'];
      let measureSummary: any[] = [];
      if (measureSheet) {
        const msData = XLSX.utils.sheet_to_json(measureSheet, { header: 1 });
        let headerIndexMap: any = {};
        let headers: string[] = [];
        const normalize = (val: any) =>
          (val || '').toString().trim().toLowerCase();

        for (let i = 0; i < msData.length; i++) {
          const row: any = msData[i];
          if (!row || row.length === 0) continue;

          const normalizedRow = row.map((cell: any) => normalize(cell));
          if (normalizedRow.includes('measure_name')) {
            headers = row;

            row.forEach((header: string, index: number) => {
              headerIndexMap[header] = index;
            });

            // read table rows
            for (let j = i + 1; j < msData.length; j++) {
              const dataRow: any = msData[j];
              if (!dataRow || dataRow.length === 0) continue;

              const measureName = dataRow[headerIndexMap['MEASURE_NAME']];
              //skip invalid rows but NOT break
              if (
                !measureName ||
                typeof measureName !== 'string' ||
                measureName.trim() === '' ||
                measureName.toLowerCase().includes('field') ||
                measureName.toLowerCase().includes('value') ||
                measureName.toLowerCase().includes('incentive') ||
                measureName.toLowerCase().includes('rebate')
              ) {
                continue;
              }
              const obj: any = {};
              // map ALL columns
              headers.forEach((header: string) => {
                obj[header] = this.cleanValue(
                  dataRow[headerIndexMap[header]]
                );
              });

              // add extra fields
              // obj['CUSTOMER_INCENTIVE'] = null;
              // obj['DESIGN_TEAM_INCENTIVE'] = null;
              // obj['MEASURE_COST'] = null;
              measureSummary.push(obj);
            }

            break;
          }
        }
      }

      this.finalJson = {
        rebateCalculation: rebateResult,
        measureSummary: measureSummary
      };

      this.selectedFile = target.files[0];
    };

    reader.readAsDataURL(target.files[0]);
  }


  cleanValue(value: any): any {
    if (value === undefined || value === null) return null;

    const str = value.toString().trim();
    // Check if it's "$ -", "$   -", "$    -", or similar
    const isDollarDash = /^\$\s*-+$/.test(str);
    const isOnlyDash = /^-+$/.test(str);
    const isEmpty = str === '';
    if (isDollarDash || isOnlyDash || isEmpty) return null;
    return str;
  }

  workSheetUploadDocument() {
    this.spinnerService.show();
    
    // Call parent component callback first and wait for it to complete
    if (this.dialogData?.onSaveCallback && typeof this.dialogData.onSaveCallback === 'function') {
      Promise.resolve(this.dialogData.onSaveCallback(this.dialogData.response))
        .then(() => {
          // ONLY after callback succeeds, call the API
          this.callSaveWorkSheetFileUpload();
        })
        .catch((error) => {
          this.spinnerService.hide();
          this.isUploading = false;
          Swal.fire({
            text: 'Error in callback: ' + (error?.message || 'Unknown error'),
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            this.dialogRef.close(true);
          });
        });
    } else {
      // If no callback, call API directly
      this.callSaveWorkSheetFileUpload();
    }
  }

  private callSaveWorkSheetFileUpload() {
    this.workorderService.saveWorkSheetFileUpload(this.finalJson,
      this.utilityService.currentUtility.OfficeId, this.workorderService.currentWorkorderID,
      this.utilityService.currentUtility.LoggedInUserRole,
      this.dialogData.response.SubGroup, this.dialogData.response.SubGroup1
    ).subscribe({
      next: (res) => {
        this.spinnerService.hide();
        this.isUploading = false;
        const response = JSON.parse(res);
        Swal.fire({
          text: response.Message,
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          this.spinnerService.show();
          let body =
          {
            File: this.selectedFile.name,
            ImageCode: this.utilityService.currentUtility.OfficeId.toLocaleLowerCase() + 'mesavw',
            User: this.userService.dbBBCSId ? this.userService.dbBBCSId : this.utilityService.currentUtility.BBCSUserId,
            Content: this.fileRenderResult?.split(",")[1],
          }

          this.workorderService
            .uploadFile(this.selectedFile.name, body, this.workorderService.currentWorkorderID)
            .subscribe((res: any) => {
              if (res.Success) {
                Swal.fire({
                  text: "File uploaded successfully!",
                  icon: "success",
                  confirmButtonText: "OK",
                }).then(() => {
                  this.dialogRef.close(true);
                });
              } else {
                Swal.fire({
                  text: res.ErrorMessage,
                  icon: "error",
                  confirmButtonText: "OK",
                }).then(() => {
                  this.dialogRef.close(true);
                });
              }
              this.spinnerService.hide();
            });
        });
      },
      error: () => {
        this.spinnerService.hide();
        this.isUploading = false;
        Swal.fire({
          text: 'An error occurred during import',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          this.dialogRef.close(true);
        });
      },
    })
  }

  uploadDocument(): void {
    if (!this.selectedFile || !this.fileExtension) {
      Swal.fire({
        text: 'Please select a file to upload',
        icon: 'warning',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const validExtensions = ['csv', 'xlsx', 'xls'];
    if (validExtensions.includes(this.fileExtension)) {
      this.isUploading = true; // Set loading state
      this.spinnerService.show();

      switch (this.dialogData.caption) {
        case 'Bulk Upload Store':
        case 'Bulk Upload SKUs':
          this.importStoreOrSKU();
          break;
        case 'IMPORT MIDSTREAM EQUIPMENT':
        case 'IMPORT BULK CONTRACTORS':
        case 'IMPORT BULK DISTRIBUTORS':
        case 'IMPORT WASHINGTON GAS ACCOUNTS':
          this.importEquipmentOrUser();
          break;
        case 'Import Report':
          this.importBulkMeasure();
          break;
        default:
          this.spinnerService.hide();
          this.isUploading = false;
          Swal.fire({
            text: 'Invalid upload type',
            icon: 'error',
            confirmButtonText: 'OK',
          });
      }
    } else {
      Swal.fire({
        text: 'Please upload a CSV or Excel file only',
        icon: 'warning',
        showConfirmButton: false,
        timer: 2000,
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  private importStoreOrSKU(): void {
    if (this.dialogData.caption === 'Bulk Upload Store') {
      let invalidDistricts = '';
      let missingDistricts = '';
      const districts = this.distributorService?.indDetail?.StoresDetails[0]?.Individuals?.Districts;

      this.excelData.forEach((data: any) => {
        const foundDistrict = districts.find(
          (district: any) => district.Value?.toLowerCase() === data.district?.toLowerCase()
        );

        if (!foundDistrict) {
          if (data.district?.length > 0) {
            if (!invalidDistricts.includes(data.district)) {
              invalidDistricts += `${data.district}, `;
            }
          } else {
            missingDistricts += `${data.storenumber}, `;
          }
        } else {
          data.district = foundDistrict.Id;
        }
      });

      let errorMessage = '';
      if (invalidDistricts.length > 0) {
        errorMessage += `The following districts are not part of this office:<br><strong>${invalidDistricts.slice(
          0,
          -2
        )}</strong><br><br>`;
      }
      if (missingDistricts.length > 0) {
        errorMessage += `The following store numbers do not have a district assigned:<br><strong>${missingDistricts.slice(
          0,
          -2
        )}</strong><br><br>`;
      }

      if (errorMessage) {
        this.spinnerService.hide();
        this.isUploading = false;
        Swal.fire({
          html: `${errorMessage}Please correct and re-upload`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
        this.dialogRef.close();
        return;
      }
    }

    const isStore = this.dialogData.caption === 'Bulk Upload Store';
    const payload = isStore
      ? {
        Utility: this.distributorService.selectedUtility,
        CompanyName: this.distributorService.selectedCompany,
        CompanyAbbr: this.distributorService.selectedCompanyAbbr,
        UserId:
          this.userService.dbBBCSId ? this.userService.dbBBCSId : this.utilityService.currentUtility.BBCSUserId,
        Storejson: JSON.stringify(this.excelData),
      }
      : {
        Utility: this.distributorService.selectedUtility,
        UserId:
          this.userService.dbBBCSId ? this.userService.dbBBCSId : this.utilityService.currentUtility.BBCSUserId,
        Skujson: JSON.stringify(this.excelData),
      };

    this.distributorService.importStoreSKU(payload, isStore).subscribe({
      next: (response) => {
        if (response.Action) {
          const serviceCall = isStore
            ? this.distributorService.getStoresOnCompany()
            : this.distributorService.getSKUOnDistributor();

          serviceCall.subscribe({
            next: (data) => {
              this.spinnerService.hide();
              this.isUploading = false;
              Swal.fire({
                text: response.Message,
                icon: 'success',
                confirmButtonText: 'OK',
              });
              if (isStore) {
                this.distributorService.indDetail.StoresDetails = data.StoresDetails;
              }
              this.dialogRef.close(true);
            },
            error: () => {
              this.spinnerService.hide();
              this.isUploading = false;
              Swal.fire({
                text: 'Failed to refresh data',
                icon: 'error',
                confirmButtonText: 'OK',
              });
              this.dialogRef.close();
            },
          });
        } else {
          this.spinnerService.hide();
          this.isUploading = false;
          Swal.fire({
            text: response.Message,
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            this.dialogRef.close();
          });
        }
      },
      error: () => {
        this.spinnerService.hide();
        this.isUploading = false;
        Swal.fire({
          text: 'An error occurred during import',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          this.dialogRef.close();
        });
      },
    });
  }

  private importEquipmentOrUser(): void {
    const payload = {
      importJson: JSON.stringify(this.excelData),
      importType: this.dialogData.caption,
    };

    this.distributorService.importEquipmentUser(payload).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.isUploading = false;
        Swal.fire({
          text: response.Message,
          icon: response.Action ? 'success' : 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          this.dialogRef.close(true);
        });
      },
      error: () => {
        this.spinnerService.hide();
        this.isUploading = false;
        Swal.fire({
          text: 'An error occurred during import',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          this.dialogRef.close();
        });
      },
    });
  }

  private importBulkMeasure(): void {
    let fromDate = '';
    let toDate = '';
    let description = '';
    let totalRebate = '';

    const sections = this.dialogData.response?.SectionInfo;
    sections?.forEach((section: any) => {
      if (section?.SectionId === 'INVOICEIMPORT') {
        const attributes = section?.DetailedInfo[0]?.Attributes;
        attributes?.forEach((attr: any) => {
          if (attr.AttributeId === 'AppStartDate') {
            fromDate = attr.SelectedValue
              ? formatDate(attr.SelectedValue, 'dd-MM-yyyy', 'en-US')
              : '';
          }
          if (attr.AttributeId === 'AppEndDate') {
            toDate = attr.SelectedValue
              ? formatDate(attr.SelectedValue, 'dd-MM-yyyy', 'en-US')
              : '';
          }
          if (attr.AttributeId === 'Description') {
            description = attr.SelectedValue;
          }
          if (attr.AttributeId === 'TotalRebate') {
            totalRebate = attr.SelectedValue;
          }
        });
      }
    });

    // Call parent component callback first and wait for it to complete
    if (this.dialogData?.onSaveCallback && typeof this.dialogData.onSaveCallback === 'function') {
      Promise.resolve(this.dialogData.onSaveCallback(this.dialogData.response))
        .then(() => {
          // ONLY after callback succeeds, call the API
          this.callSaveMeasureSalesImport(fromDate, toDate, description, totalRebate);
        })
        .catch((error) => {
          this.spinnerService.hide();
          this.isUploading = false;
          Swal.fire({
            text: 'Error in callback: ' + (error?.message || 'Unknown error'),
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            this.dialogRef.close(true);
          });
        });
    } else {
      // If no callback, call API directly
      this.callSaveMeasureSalesImport(fromDate, toDate, description, totalRebate);
    }
  }

  private callSaveMeasureSalesImport(fromDate: string, toDate: string, description: string, totalRebate: string) {
    const fileContentBase64 = this.fileContent?.split(',')[1] || '';
    const payload = {
      File: this.selectedFile?.name,
      Invoicejson: JSON.stringify(this.excelData),
      Content: fileContentBase64,
      Description: description,
      FromDate: fromDate,
      ToDate: toDate,
      TotalRebate: totalRebate,
    };

    // After callback, call the API
    this.workorderService
      .saveMeasureSalesImport(
        payload,
        this.dialogData.response.WorkorderId,
        this.dialogData.response.SubGroup,
        this.dialogData.response.SubGroup1
      )
      .subscribe({
        next: (response) => {
          this.spinnerService.hide();
          this.isUploading = false;
          Swal.fire({
            text: response.Message,
            icon: response.Action ? 'success' : 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            this.dialogRef.close(true);
          });
        },
        error: () => {
          this.spinnerService.hide();
          this.isUploading = false;
          Swal.fire({
            text: 'An error occurred during import',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            this.dialogRef.close(true);
          });
        },
      });
  }
}